import React from "react";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import { useTranslation } from "react-i18next";
import { DecisionCondition } from "@/domain/troubleshooting.interface";
import {
  isConditionNotEvaluated,
  primaryConditionIndexes,
} from "@/utils/troubleshooting.utils";

const formatConditionValue = (value: DecisionCondition["value"]): string =>
  value === null || value === undefined ? "null" : String(value);

type ConditionStatus = "passed" | "failed" | "not-evaluated";

const STATUS_META: Record<
  ConditionStatus,
  { color: string; Icon: typeof CheckIcon; labelKey: string }
> = {
  passed: { color: "success.main", Icon: CheckIcon, labelKey: "detail.passed" },
  failed: {
    color: "error.main",
    Icon: ClearIcon,
    labelKey: "detail.notPassed",
  },
  "not-evaluated": {
    color: "text.secondary",
    Icon: HorizontalRuleIcon,
    labelKey: "detail.notEvaluated",
  },
};

interface ConditionsTableProps {
  conditions: DecisionCondition[];
  reasonCode?: string;
}

/**
 * COYN-1507 AC8 / contract §7: every evaluated condition, in API order, with
 * ✓ passed and ✗ failed. Short-circuited conditions (`detail: "not
 * evaluated"`) are neutral, not failures. The failing condition(s) behind
 * the primary reason are highlighted.
 */
const ConditionsTable: React.FC<ConditionsTableProps> = ({
  conditions,
  reasonCode,
}) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string) => string;
  const primary = primaryConditionIndexes(conditions, reasonCode);

  return (
    <Table size="small" aria-label={t("detail.conditions")}>
      <TableHead>
        <TableRow>
          <TableCell>{t("detail.conditionColumns.key")}</TableCell>
          <TableCell>{t("detail.conditionColumns.value")}</TableCell>
          <TableCell>{t("detail.conditionColumns.passed")}</TableCell>
          <TableCell>{t("detail.conditionColumns.detail")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {conditions.map((condition, index) => {
          const status: ConditionStatus = condition.passed
            ? "passed"
            : isConditionNotEvaluated(condition)
              ? "not-evaluated"
              : "failed";
          const { color, Icon, labelKey } = STATUS_META[status];
          const isPrimary = primary.includes(index);
          return (
            <TableRow
              key={`${condition.key}-${index}`}
              data-testid={`condition-${condition.key}`}
              data-status={status}
              data-primary={isPrimary || undefined}
              sx={{
                ...(isPrimary && {
                  backgroundColor: "rgba(198,40,40,0.08)",
                  "& > td:first-of-type": {
                    borderLeft: "4px solid",
                    borderLeftColor: "error.main",
                  },
                }),
                ...(status === "not-evaluated" && {
                  "& > td": { color: "text.secondary" },
                  opacity: 0.75,
                }),
              }}
            >
              <TableCell sx={{ fontFamily: "monospace" }}>
                {condition.key}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>
                {status === "not-evaluated"
                  ? "-"
                  : formatConditionValue(condition.value)}
              </TableCell>
              <TableCell>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexWrap: "wrap",
                    color,
                  }}
                >
                  <Icon fontSize="small" aria-hidden />
                  <Typography variant="body2">{tt(labelKey)}</Typography>
                  {isPrimary && (
                    <Chip
                      size="small"
                      color="error"
                      label={t("detail.primaryReason")}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                {status === "not-evaluated" ? "-" : condition.detail || "-"}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default ConditionsTable;
